import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { getConsent, setConsent } from '../lib/consent';

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  const closeBanner = (persisted) => {
    setVisible(false);
    setSettingsOpen(false);
    if (typeof document !== 'undefined') {
      document.body.dataset.consentOpen = 'false';
    }
    if (persisted) {
      setAnalytics(persisted.analytics === true);
      setMarketing(persisted.marketing === true);
    }
  };

  const openBanner = () => {
    const existing = getConsent();
    setAnalytics(existing?.analytics === true);
    setMarketing(existing?.marketing === true);
    setVisible(true);
    if (typeof document !== 'undefined') {
      document.body.dataset.consentOpen = 'true';
    }
  };

  useEffect(() => {
    const existing = getConsent();
    if (!existing) {
      const timer = window.setTimeout(() => openBanner(), 600);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, []);

  useEffect(() => {
    const handler = () => openBanner();
    window.addEventListener('agencive:open-consent', handler);
    return () => window.removeEventListener('agencive:open-consent', handler);
  }, []);

  if (!visible) return null;

  const acceptAll = () => {
    setConsent({ necessary: true, analytics: true, marketing: true });
    closeBanner({ analytics: true, marketing: true });
  };
  const rejectAll = () => {
    setConsent({ necessary: true, analytics: false, marketing: false });
    closeBanner({ analytics: false, marketing: false });
  };
  const saveSettings = () => {
    setConsent({ necessary: true, analytics, marketing });
    closeBanner({ analytics, marketing });
  };

  const primaryBtn =
    'h-10 rounded-full text-[13.5px] font-semibold transition-colors flex items-center justify-center';

  return (
    <div
      className="fixed inset-x-3 bottom-3 md:bottom-5 md:inset-x-auto md:right-5 md:left-auto md:max-w-md z-[100]"
      role="dialog"
      aria-live="polite"
      aria-label="Evästesuostumus"
      data-testid="cookie-consent-banner"
    >
      <div className="rounded-2xl bg-[#171716] text-white shadow-2xl border border-white/10 p-5 md:p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[13px] uppercase tracking-widest text-white/60 font-semibold">Evästeet</div>
            <h3 className="mt-1 text-[17px] font-semibold leading-snug">Käytämme evästeitä sivuston kehittämiseen</h3>
          </div>
          <button
            type="button"
            onClick={rejectAll}
            className="text-white/60 hover:text-white p-1"
            aria-label="Sulje ja hylkää kaikki ei-välttämättömät"
            data-testid="cookie-consent-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <p className="mt-3 text-[13.5px] leading-relaxed text-white/75">
          Välttämättömät evästeet toimivat aina. Analytiikka (PostHog) ja markkinointi
          (Meta Pixel + Meta Conversions API) ovat oletuksena pois käytöstä ja latautuvat
          vain, jos hyväksyt ne. Lue lisää{' '}
          <a href="/tietosuoja" className="underline hover:text-white">tietosuojaselosteesta</a>.
        </p>

        {settingsOpen && (
          <div className="mt-4 space-y-2 border-t border-white/10 pt-4">
            <label className="flex items-start gap-3 text-[13.5px] opacity-70 cursor-not-allowed">
              <input type="checkbox" checked disabled className="mt-0.5" />
              <span>
                <span className="block font-semibold">Välttämättömät</span>
                <span className="block text-white/60">Vaadittu sivuston toiminnalle.</span>
              </span>
            </label>
            <label className="flex items-start gap-3 text-[13.5px]">
              <input
                type="checkbox"
                checked={analytics}
                onChange={(e) => setAnalytics(e.target.checked)}
                className="mt-0.5"
                data-testid="cookie-consent-analytics-checkbox"
              />
              <span>
                <span className="block font-semibold">Analytiikka</span>
                <span className="block text-white/60">PostHog kävijämittaukseen.</span>
              </span>
            </label>
            <label className="flex items-start gap-3 text-[13.5px]">
              <input
                type="checkbox"
                checked={marketing}
                onChange={(e) => setMarketing(e.target.checked)}
                className="mt-0.5"
                data-testid="cookie-consent-marketing-checkbox"
              />
              <span>
                <span className="block font-semibold">Markkinointi</span>
                <span className="block text-white/60">Meta Pixel (selain) ja Meta Conversions API (palvelin).</span>
              </span>
            </label>
          </div>
        )}

        <div className="mt-5 grid grid-cols-2 gap-2">
          {!settingsOpen ? (
            <>
              <button
                type="button"
                onClick={rejectAll}
                className={`${primaryBtn} bg-white/10 hover:bg-white/20 text-white border border-white/25`}
                data-testid="cookie-consent-reject"
              >
                Hylkää kaikki
              </button>
              <button
                type="button"
                onClick={acceptAll}
                className={`${primaryBtn} bg-white text-[#171716] hover:bg-white/90`}
                data-testid="cookie-consent-accept"
              >
                Hyväksy kaikki
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setSettingsOpen(false)}
                className={`${primaryBtn} bg-white/10 hover:bg-white/20 text-white border border-white/25`}
              >
                Takaisin
              </button>
              <button
                type="button"
                onClick={saveSettings}
                className={`${primaryBtn} bg-white text-[#171716] hover:bg-white/90`}
                data-testid="cookie-consent-save-settings"
              >
                Tallenna valinnat
              </button>
            </>
          )}
        </div>
        {!settingsOpen && (
          <button
            type="button"
            onClick={() => setSettingsOpen(true)}
            className="mt-3 text-[12.5px] underline text-white/70 hover:text-white"
            data-testid="cookie-consent-settings"
          >
            Asetukset
          </button>
        )}
      </div>
    </div>
  );
};

export default CookieConsent;
